import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSessions } from '../services/api';
import { clearSavedSession } from '../context/MatchContext';
import Flag from './ui/Flag';
import StatCard from './ui/StatCard';
import PageLayout from './ui/PageLayout';

const STORAGE_KEY       = 'tkd_session_v1';
const CLUB_ATHLETES_KEY = 'tkd_club_athletes_v1';

// ── helpers ──────────────────────────────────────────────────────────────────

function aggregateAthletes(sessions) {
  const map = {};
  sessions.forEach(s => {
    ['blue', 'red'].forEach(side => {
      const name = s[`athlete_${side}_name`];
      if (!name) return;
      const nat        = s[`athlete_${side}_nationality`] || '';
      const myScore    = s[`score_${side}`]                            ?? null;
      const oppScore   = s[`score_${side === 'blue' ? 'red' : 'blue'}`] ?? null;
      const won        = myScore !== null && oppScore !== null ? myScore > oppScore : null;
      if (!map[name]) map[name] = { name, nationality: nat, matches: 0, wins: 0, losses: 0, draws: 0, ptScored: 0, ptConceded: 0 };
      map[name].matches++;
      if (won === true)  map[name].wins++;
      if (won === false) map[name].losses++;
      if (won === null && myScore !== null) map[name].draws++;
      if (myScore  !== null) map[name].ptScored    += Number(myScore);
      if (oppScore !== null) map[name].ptConceded  += Number(oppScore);
    });
  });
  return Object.values(map).sort((a, b) => b.matches - a.matches);
}

// ── DashboardPage ─────────────────────────────────────────────────────────────

function DashboardPage() {
  const navigate = useNavigate();

  const [sessions,       setSessions]       = useState([]);
  const [loadingSess,    setLoadingSess]    = useState(true);
  const [activeSession,  setActiveSession]  = useState(null);
  const [clubAthletes,   setClubAthletes]   = useState([]);
  const [newName,        setNewName]        = useState('');
  const [archiveExpanded, setArchiveExpanded] = useState(false);

  // Check active local session
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved?.isSetupComplete && saved?.session) setActiveSession(saved.session);
      }
    } catch {}

    try {
      const raw = localStorage.getItem(CLUB_ATHLETES_KEY);
      if (raw) setClubAthletes(JSON.parse(raw));
    } catch {}

    getSessions()
      .then(data => setSessions(Array.isArray(data) ? data : []))
      .catch(() => setSessions([]))
      .finally(() => setLoadingSess(false));
  }, []);

  const dbAthletes = aggregateAthletes(sessions);
  const displayedSessions = archiveExpanded ? sessions : sessions.slice(0, 8);

  function handleNewAnalysis() {
    clearSavedSession();
    navigate('/analisi');
  }

  function handleResume() {
    navigate('/analisi');
  }

  function addClubAthlete() {
    if (!newName.trim()) return;
    const updated = [...clubAthletes, { name: newName.trim(), id: Date.now() }];
    setClubAthletes(updated);
    localStorage.setItem(CLUB_ATHLETES_KEY, JSON.stringify(updated));
    setNewName('');
  }

  function removeClubAthlete(id) {
    const updated = clubAthletes.filter(a => a.id !== id);
    setClubAthletes(updated);
    localStorage.setItem(CLUB_ATHLETES_KEY, JSON.stringify(updated));
  }

  const totalWins  = dbAthletes.reduce((s, a) => s + a.wins,    0);
  const totalPts   = dbAthletes.reduce((s, a) => s + a.ptScored, 0);

  const header = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 28, height: 28, background: 'var(--accent)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>A</div>
        <span style={{ fontWeight: 700, fontSize: 15, letterSpacing: -0.3 }}>ATC Analytics</span>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {activeSession && (
          <button className="btn btn-secondary" onClick={handleResume}>
            Riprendi analisi →
          </button>
        )}
        <button className="btn btn-accent" onClick={handleNewAnalysis}>
          + Analizza nuovo video
        </button>
      </div>
    </>
  );

  return (
    <PageLayout header={header}>

        {/* ── Sessione in corso ────────────────────────────────────────────── */}
        {activeSession && (
          <div style={{
            background: 'linear-gradient(135deg, rgba(232,81,106,0.12), rgba(59,130,246,0.08))',
            border: '1px solid var(--accent)',
            borderRadius: 'var(--radius-lg)', padding: '14px 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginBottom: 24,
          }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 600, marginBottom: 4 }}>ANALISI IN CORSO</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>
                <Flag nat={activeSession.athlete_blue_nationality} />
                <span style={{ color: 'var(--color-blue)' }}>{activeSession.athlete_blue_name}</span>
                <span style={{ color: 'var(--color-text-dim)', margin: '0 8px' }}>vs</span>
                <Flag nat={activeSession.athlete_red_nationality} />
                <span style={{ color: 'var(--color-red)' }}>{activeSession.athlete_red_name}</span>
              </div>
              {activeSession.event_name && (
                <div style={{ fontSize: 11, color: 'var(--color-text-dim)', marginTop: 3 }}>{activeSession.event_name} · {activeSession.event_date}</div>
              )}
            </div>
            <button className="btn btn-accent" onClick={handleResume}>
              Riprendi →
            </button>
          </div>
        )}

        {/* ── Stats globali ────────────────────────────────────────────────── */}
        <div className="ds-stat-row" style={{ marginBottom: 28 }}>
          <StatCard label="Incontri analizzati" value={loadingSess ? '—' : sessions.length} />
          <StatCard label="Atleti nel database" value={loadingSess ? '—' : dbAthletes.length} />
          <StatCard label="Vittorie totali tracciate" value={loadingSess ? '—' : totalWins} />
          <StatCard label="Punti totali segnati" value={loadingSess ? '—' : totalPts} />
        </div>

        {/* ── Colonne principali ───────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, marginBottom: 28 }}>

          {/* ── Archivio incontri ─────────────────────────────────────────── */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div className="ds-section-label">ARCHIVIO INCONTRI</div>
              {sessions.length > 0 && (
                <button
                  style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 12, cursor: 'pointer', padding: 0 }}
                  onClick={() => navigate('/archivio')}
                >
                  Vedi tutti ({sessions.length}) →
                </button>
              )}
            </div>
            {loadingSess ? (
              <div className="text-dim" style={{ fontSize: 13, padding: 20 }}>Caricamento…</div>
            ) : sessions.length === 0 ? (
              <div className="ds-card ds-empty-state">
                Nessun incontro analizzato.<br />
                <span style={{ color: 'var(--accent)', cursor: 'pointer' }} onClick={handleNewAnalysis}>Analizza il tuo primo video →</span>
              </div>
            ) : (
              <>
                {displayedSessions.map(s => {
                  const blueWon = s.score_blue != null && s.score_red != null && s.score_blue > s.score_red;
                  const redWon  = s.score_blue != null && s.score_red != null && s.score_red  > s.score_blue;
                  return (
                    <div key={s.id} style={{
                      background: 'var(--bg-card)', border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius)', padding: '12px 16px', marginBottom: 8,
                      display: 'flex', alignItems: 'center', gap: 12,
                    }}>
                      {/* Atleti */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <Flag nat={s.athlete_blue_nationality} />
                          <span style={{ color: blueWon ? 'var(--color-blue)' : 'var(--color-text)' }}>{s.athlete_blue_name}</span>
                          <span style={{ color: 'var(--color-text-dim)', fontSize: 11 }}>vs</span>
                          <Flag nat={s.athlete_red_nationality} />
                          <span style={{ color: redWon ? 'var(--color-red)' : 'var(--color-text)' }}>{s.athlete_red_name}</span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--color-text-dim)', marginTop: 3 }}>
                          {s.event_name && <span>{s.event_name}</span>}
                          {s.weight_category && <span style={{ marginLeft: 6 }}>{s.weight_category}</span>}
                          {s.event_date && <span style={{ marginLeft: 6 }}>{s.event_date}</span>}
                        </div>
                      </div>

                      {/* Score */}
                      {s.score_blue != null && (
                        <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: -0.5, whiteSpace: 'nowrap' }}>
                          <span style={{ color: 'var(--color-blue)' }}>{s.score_blue}</span>
                          <span style={{ color: 'var(--color-text-dim)', margin: '0 4px', fontSize: 12 }}>:</span>
                          <span style={{ color: 'var(--color-red)' }}>{s.score_red}</span>
                        </div>
                      )}

                      {/* Actions */}
                      {s.id && (
                        <button
                          style={{ background: 'var(--bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-sub)', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 11, whiteSpace: 'nowrap' }}
                          onClick={() => navigate(`/report/${s.id}`)}
                        >
                          Report
                        </button>
                      )}
                    </div>
                  );
                })}
                {sessions.length > 8 && (
                  <button
                    style={{ background: 'none', border: '1px solid var(--color-border)', color: 'var(--color-text-dim)', borderRadius: 6, padding: '7px 16px', cursor: 'pointer', fontSize: 12, width: '100%', marginTop: 4 }}
                    onClick={() => setArchiveExpanded(v => !v)}
                  >
                    {archiveExpanded ? 'Mostra meno' : `Mostra tutti (${sessions.length})`}
                  </button>
                )}
              </>
            )}
          </div>

          {/* ── Atleti del club ─────────────────────────────────────────────── */}
          <div>
            <div className="ds-section-label" style={{ marginBottom: 12 }}>ATLETI DEL CLUB</div>
            <div className="ds-card" style={{ padding: 16 }}>
              {/* Add */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <input
                  style={{ flex: 1, background: 'var(--bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 6, padding: '7px 10px', color: 'var(--color-text)', fontSize: 12, outline: 'none' }}
                  placeholder="Nome atleta…"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addClubAthlete()}
                />
                <button
                  style={{ background: 'var(--accent)', border: 'none', color: '#fff', borderRadius: 6, padding: '7px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}
                  onClick={addClubAthlete}
                >+</button>
              </div>

              {/* List */}
              {clubAthletes.length === 0 ? (
                <div style={{ fontSize: 12, color: 'var(--color-text-dim)', textAlign: 'center', padding: '12px 0' }}>
                  Nessun atleta aggiunto
                </div>
              ) : (
                clubAthletes.map(a => {
                  const dbEntry = dbAthletes.find(d => d.name.toLowerCase() === a.name.toLowerCase());
                  return (
                    <div key={a.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '7px 0', borderBottom: '1px solid var(--color-border)',
                    }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{a.name}</div>
                        {dbEntry ? (
                          <div style={{ fontSize: 10, color: 'var(--color-text-dim)', marginTop: 1 }}>
                            {dbEntry.matches} incontri · {dbEntry.wins}V {dbEntry.losses}P
                          </div>
                        ) : (
                          <div style={{ fontSize: 10, color: 'var(--color-text-dim)', marginTop: 1 }}>nessun dato</div>
                        )}
                      </div>
                      <button
                        style={{ background: 'none', border: 'none', color: 'var(--color-text-dim)', cursor: 'pointer', fontSize: 16, padding: '0 4px', lineHeight: 1 }}
                        onClick={() => removeClubAthlete(a.id)}
                        title="Rimuovi"
                      >×</button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* ── Database atleti ──────────────────────────────────────────────── */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div className="ds-section-label">DATABASE ATLETI</div>
            {dbAthletes.length > 0 && (
              <button
                style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 12, cursor: 'pointer', padding: 0 }}
                onClick={() => navigate('/atleti')}
              >
                Vedi tutti ({dbAthletes.length}) →
              </button>
            )}
          </div>
          {dbAthletes.length === 0 ? (
            <div className="ds-card ds-empty-state">
              Nessun dato disponibile. Gli atleti appariranno qui dopo le prime analisi.
            </div>
          ) : (
            <div className="ds-card" style={{ overflow: 'hidden' }}>
              {/* Header */}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 60px 80px 80px 90px 90px 80px', gap: 0, padding: '10px 16px', borderBottom: '1px solid var(--color-border)', fontSize: 10, fontWeight: 700, color: 'var(--color-text-dim)', letterSpacing: 0.8 }}>
                <span>ATLETA</span>
                <span style={{ textAlign: 'center' }}>INC</span>
                <span style={{ textAlign: 'center' }}>VINTI</span>
                <span style={{ textAlign: 'center' }}>PERSI</span>
                <span style={{ textAlign: 'center' }}>% VITT</span>
                <span style={{ textAlign: 'center' }}>PT FATTI</span>
                <span style={{ textAlign: 'center' }}>PT SUBITI</span>
              </div>
              {dbAthletes.map((a, i) => {
                const winRate = a.matches > 0 ? Math.round((a.wins / a.matches) * 100) : 0;
                const isClub  = clubAthletes.some(c => c.name.toLowerCase() === a.name.toLowerCase());
                return (
                  <div key={a.name} style={{
                    display: 'grid', gridTemplateColumns: '2fr 60px 80px 80px 90px 90px 80px',
                    gap: 0, padding: '10px 16px',
                    borderBottom: i < dbAthletes.length - 1 ? '1px solid var(--color-border)' : 'none',
                    alignItems: 'center', cursor: 'pointer', transition: 'background 0.15s',
                  }}
                  onClick={() => navigate(`/atleti/${encodeURIComponent(a.name)}`)}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Flag nat={a.nationality} />
                      <div>
                        <span style={{ fontSize: 13, fontWeight: 600 }}>{a.name}</span>
                        {isClub && <span style={{ marginLeft: 6, fontSize: 9, background: 'var(--accent-bg)', color: 'var(--accent)', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>CLUB</span>}
                      </div>
                    </div>
                    <span style={{ textAlign: 'center', fontSize: 13 }}>{a.matches}</span>
                    <span style={{ textAlign: 'center', fontSize: 13, color: 'var(--color-green)', fontWeight: 600 }}>{a.wins}</span>
                    <span style={{ textAlign: 'center', fontSize: 13, color: 'var(--color-red)' }}>{a.losses}</span>
                    <span style={{ textAlign: 'center', fontSize: 13, color: winRate >= 60 ? 'var(--color-green)' : winRate >= 40 ? 'var(--color-orange)' : 'var(--color-red)', fontWeight: 600 }}>
                      {a.wins + a.losses > 0 ? `${winRate}%` : '—'}
                    </span>
                    <span style={{ textAlign: 'center', fontSize: 13, color: 'var(--color-blue)' }}>{a.ptScored || '—'}</span>
                    <span style={{ textAlign: 'center', fontSize: 13, color: 'var(--color-text-dim)' }}>{a.ptConceded || '—'}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

    </PageLayout>
  );
}

export default DashboardPage;
